import { PageHero, SectionHeader } from "@/components/Section";
import useReveal from "@/hooks/useReveal";
import { Link } from "react-router-dom";
import {
  Baby,
  Smartphone,
  HandHeart,
  Megaphone,
  BookOpen,
  MapPin,
  ArrowRight,
  Flame,
  Sparkles,
} from "lucide-react";

const pillars = [
  {
    icon: Baby,
    title: "Next Generation",
    body:
      "Discipling children, youth, and young adults with biblical truth, mentoring, and creative spaces where their gifts are called out early and grown for the Kingdom.",
  },
  {
    icon: Smartphone,
    title: "Digital Ministry",
    body:
      "Extending the ministry of the Word beyond the sanctuary — live-stream worship, on-demand sermons, digital devotionals, and connection tools that keep the church family close.",
  },
  {
    icon: HandHeart,
    title: "Community Outreach",
    body:
      "Loving Memphis in tangible ways — food, mercy, mentorship, education, and partnership with neighbors — until the city knows Christ through the works of His people.",
  },
  {
    icon: Megaphone,
    title: "Evangelism",
    body:
      "Boldly proclaiming the gospel of Jesus Christ — house to house, block to block, and heart to heart — that many more may be saved.",
  },
  {
    icon: BookOpen,
    title: "Discipleship",
    body:
      "Grounding believers in the whole counsel of God through Sunday School, mid-week Bible study, small groups, and mentoring relationships that produce mature followers of Christ.",
  },
  {
    icon: MapPin,
    title: "Vision for Memphis",
    body:
      "A church rooted in East Memphis, sent to bless the whole city — schools, families, neighborhoods, and the next generation of Memphis leaders.",
  },
];

export default function Future() {
  useReveal();

  return (
    <div data-testid="future-page">
      <PageHero
        eyebrow="Our Future"
        title="Our greatest days are still ahead."
        subtitle="God has been faithful for over 150 years. We believe the next chapter of New Philadelphia will honor everything He has built — and reach further than we've ever imagined."
        image="https://images.pexels.com/photos/7520354/pexels-photo-7520354.jpeg?auto=compress&cs=tinysrgb&w=2000"
      />

      {/* Torch statement */}
      <section className="bg-white py-24">
        <div className="container-npbc grid lg:grid-cols-[1.1fr_1fr] gap-14 items-center">
          <div data-reveal>
            <div className="eyebrow">The Torch</div>
            <h2 className="mt-3 heading-serif text-5xl leading-[1.02] text-balance">
              "For more than 150 years, each generation has received the torch of faith from those who came before them."
            </h2>
            <p className="mt-6 text-npbc-gray leading-relaxed text-[17px]">
              Today, we are honored to carry that torch forward — trusting God to write the next chapter
              of New Philadelphia's story. Not by leaving behind the ancient path, but by walking it with
              fresh fire, fresh vision, and a fresh generation.
            </p>
            <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-npbc-gold/40 bg-npbc-cream px-4 py-2">
              <Flame className="h-4 w-4 text-npbc-red" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-npbc-black">
                Historic. Forward-looking. Faithful.
              </span>
            </div>
          </div>
          <div data-reveal className="relative">
            <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-npbc-red/15 to-npbc-gold/15 blur-xl" />
            <img
              src="https://images.pexels.com/photos/7520361/pexels-photo-7520361.jpeg?auto=compress&cs=tinysrgb&w=1400"
              alt="Congregation lifted in praise"
              className="relative rounded-3xl w-full h-[520px] object-cover shadow-2xl"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Six pillars of our future */}
      <section className="bg-npbc-cream py-24">
        <div className="container-npbc">
          <div data-reveal>
            <SectionHeader
              eyebrow="Six Pillars of Our Future"
              title="Where we are pressing forward."
              subtitle="These are the priorities God has laid on our hearts for this next chapter — anchored in Scripture, shaped by our heritage, and stretched toward what only He can do."
            />
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {pillars.map((p, i) => (
              <div
                key={p.title}
                data-reveal
                style={{ transitionDelay: `${i * 60}ms` }}
                className="group card-elevated p-8 hover:-translate-y-1 transition-transform duration-500"
              >
                <div className="h-12 w-12 rounded-2xl bg-npbc-red/10 text-npbc-red grid place-items-center group-hover:bg-npbc-red group-hover:text-white transition">
                  <p.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-6 heading-serif text-2xl">{p.title}</h3>
                <p className="mt-2 text-npbc-gray leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Prayer of vision */}
      <section className="bg-npbc-black text-white py-24 relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/7520354/pexels-photo-7520354.jpeg?auto=compress&cs=tinysrgb&w=2000"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-25"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-npbc-black via-npbc-black/85 to-npbc-black/60" />
        <div className="container-npbc relative max-w-3xl mx-auto text-center" data-reveal>
          <div className="eyebrow !text-npbc-gold justify-center flex">Our Prayer</div>
          <h2 className="mt-4 heading-serif text-4xl md:text-5xl leading-[1.1]">
            Do it again, Lord. And do a new thing.
          </h2>
          <p className="mt-6 text-white/80 text-lg leading-relaxed">
            That the God who built this house in the days after Emancipation would build it again in
            our day — reaching families, restoring homes, discipling the next generation, and shining as
            a city on a hill in Memphis.
          </p>
          <blockquote className="mt-10 inline-block border-l-2 border-npbc-gold pl-5 italic text-white/90 max-w-xl text-left">
            "See, I am doing a new thing! Now it springs up; do you not perceive it? I am making a way
            in the wilderness and streams in the wasteland."
            <span className="block mt-3 not-italic text-xs uppercase tracking-[0.24em] text-npbc-gold">
              Isaiah 43:19
            </span>
          </blockquote>
        </div>
      </section>

      {/* Be part of the next chapter */}
      <section className="bg-white py-24">
        <div className="container-npbc grid lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
          <div data-reveal>
            <div className="eyebrow">Be Part of the Next Chapter</div>
            <h2 className="mt-3 heading-serif text-5xl leading-[1.05]">
              The story continues — and there's a chapter for you.
            </h2>
            <p className="mt-5 text-npbc-gray text-[17px] leading-relaxed">
              Whether God is calling you to <span className="font-semibold text-npbc-black">visit</span>,
              <span className="font-semibold text-npbc-black"> volunteer</span>,
              <span className="font-semibold text-npbc-black"> give</span>, or
              <span className="font-semibold text-npbc-black"> serve</span> — you were not brought here
              by accident. Come and see what God is doing at New Philadelphia today.
            </p>
          </div>
          <div data-reveal className="flex flex-col gap-3">
            <Link to="/visit" data-testid="future-visit-cta" className="btn-primary justify-center">
              Plan Your Visit <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/give" data-testid="future-give-cta" className="btn-gold justify-center">
              Give to the Vision <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" data-testid="future-serve-cta" className="btn-ghost-dark justify-center">
              Volunteer / Serve <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Sparkling closing */}
      <section className="bg-npbc-cream py-16">
        <div className="container-npbc text-center max-w-2xl mx-auto" data-reveal>
          <Sparkles className="mx-auto h-6 w-6 text-npbc-red" />
          <p className="mt-4 heading-serif text-2xl md:text-3xl text-npbc-black leading-snug italic">
            "New Philadelphia is both historic and forward-looking — a church where tradition meets
            transformation."
          </p>
        </div>
      </section>
    </div>
  );
}
